(() => {
  'use strict';

  const REQUIRED_PHOTOS = 9;
  const script = document.currentScript;
  const PRODUCT_ASSET = script?.dataset.productAsset || '';
  const state = {
    photos: Array(REQUIRED_PHOTOS).fill(null),
    busy: false,
    productImage: null,
    productReady: false,
    renderQueued: false
  };

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const photoCount = () => state.photos.filter(Boolean).length;
  const isSupportedImage = file => /^image\/(jpeg|png|webp)$/i.test(file?.type || '');

  function setError(message = '') {
    const error = $('[data-mc-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Image could not be opened'));
      image.src = url;
    });
  }

  async function createPhoto(file) {
    const url = URL.createObjectURL(file);
    try {
      return { file, url, image: await loadImage(url) };
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  }

  function releasePhoto(photo) {
    if (photo?.url) URL.revokeObjectURL(photo.url);
  }

  async function acceptFiles(fileList) {
    if (state.busy) return;
    const files = [...(fileList || [])].filter(isSupportedImage);
    if (!files.length) {
      setError('Please choose JPG, PNG or WEBP images.');
      return;
    }

    const emptyIndexes = state.photos
      .map((photo, index) => photo ? null : index)
      .filter(index => index !== null);
    const selected = files.slice(0, emptyIndexes.length);
    setError(files.length > emptyIndexes.length ? 'Only 9 photos can be previewed at once.' : '');
    state.busy = true;
    try {
      for (let index = 0; index < selected.length; index += 1) {
        state.photos[emptyIndexes[index]] = await createPhoto(selected[index]);
      }
    } catch (error) {
      setError('One of the selected images could not be read. Please try another file.');
    } finally {
      state.busy = false;
      renderAll();
    }
  }

  function removePhoto(index) {
    if (state.busy || !state.photos[index]) return;
    releasePhoto(state.photos[index]);
    state.photos[index] = null;
    renderAll();
  }

  function cropSource(photo) {
    const width = photo.image.naturalWidth;
    const height = photo.image.naturalHeight;
    const size = Math.min(width, height);
    return { sx: (width - size) / 2, sy: (height - size) / 2, size };
  }

  function drawPhoto(ctx, photo, x, y, width, height, circle = false) {
    if (!photo?.image) return;
    const source = cropSource(photo);
    ctx.save();
    if (circle) {
      ctx.beginPath();
      ctx.arc(x + width / 2, y + height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
      ctx.clip();
    }
    ctx.drawImage(photo.image, source.sx, source.sy, source.size, source.size, x, y, width, height);
    ctx.restore();
  }

  function drawRoundedRect(ctx, x, y, width, height, radius, fill, stroke) {
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, width, height, radius);
    else ctx.rect(x, y, width, height);
    if (fill) { ctx.fillStyle = fill; ctx.fill(); }
    if (stroke) { ctx.strokeStyle = stroke; ctx.stroke(); }
  }

  function drawPlaceholder(ctx, x, y, width, height, number, circle = false) {
    ctx.save();
    ctx.fillStyle = '#e8ddd0';
    ctx.strokeStyle = 'rgba(95,70,48,.2)';
    ctx.lineWidth = 2;
    if (circle) {
      ctx.beginPath();
      ctx.arc(x + width / 2, y + height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else {
      ctx.fillRect(x, y, width, height);
      ctx.strokeRect(x, y, width, height);
    }
    ctx.fillStyle = '#9f8d7c';
    ctx.font = `700 ${Math.max(16, width * .16)}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(number).padStart(2, '0'), x + width / 2, y + height / 2);
    ctx.restore();
  }

  function drawVinyl(ctx, photo, cx, cy, radius, number) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.shadowColor = 'rgba(52,34,21,.28)';
    ctx.shadowBlur = 16;
    ctx.shadowOffsetY = 10;
    const gradient = ctx.createRadialGradient(-radius * .2, -radius * .24, radius * .06, 0, 0, radius);
    gradient.addColorStop(0, '#292929');
    gradient.addColorStop(.45, '#070707');
    gradient.addColorStop(.76, '#181818');
    gradient.addColorStop(1, '#020202');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = 'rgba(255,255,255,.07)';
    ctx.lineWidth = 1;
    for (let ring = radius * .6; ring < radius * .94; ring += radius * .045) {
      ctx.beginPath();
      ctx.arc(0, 0, ring, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
    const labelRadius = radius * .57;
    if (photo) drawPhoto(ctx, photo, cx - labelRadius, cy - labelRadius, labelRadius * 2, labelRadius * 2, true);
    else drawPlaceholder(ctx, cx - labelRadius, cy - labelRadius, labelRadius * 2, labelRadius * 2, number, true);
  }

  function drawArcText(ctx, text, centerX, centerY, radius, fontSize) {
    ctx.save();
    ctx.font = `600 ${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const chars = [...text];
    const advances = chars.map(char => Math.max(ctx.measureText(char).width, fontSize * .22) + fontSize * .005);
    let angle = advances.reduce((sum, value) => sum + value, 0) / radius / 2;
    chars.forEach((char, index) => {
      const advance = advances[index] / radius;
      angle -= advance / 2;
      ctx.save();
      ctx.translate(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius);
      ctx.rotate(angle - Math.PI / 2);
      ctx.fillText(char, 0, 0);
      ctx.restore();
      angle -= advance / 2;
    });
    ctx.restore();
  }

  function renderCanvas() {
    state.renderQueued = false;
    const canvas = $('[data-mc-canvas]');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const photos = state.photos;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const background = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    background.addColorStop(0, '#f8f1e8');
    background.addColorStop(1, '#efe1d1');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#151311';
    ctx.font = '800 42px Arial, sans-serif';
    ctx.fillText('YOUR CUSTOM MUSIC BOX', 66, 72);
    ctx.fillStyle = '#776d64';
    ctx.font = '500 20px Arial, sans-serif';
    ctx.fillText('A live placement preview using your selected photos', 68, 105);

    const crop = { sx: 45, sy: 410, sw: 525, sh: 630 };
    const product = { x: 62, y: 135, w: 720, h: 864 };
    if (state.productReady) {
      ctx.save();
      ctx.shadowColor = 'rgba(65,46,28,.2)';
      ctx.shadowBlur = 28;
      ctx.shadowOffsetY = 18;
      ctx.drawImage(state.productImage, crop.sx, crop.sy, crop.sw, crop.sh, product.x, product.y, product.w, product.h);
      ctx.restore();
    } else drawRoundedRect(ctx, product.x, product.y, product.w, product.h, 22, '#fff', '#d7c8b8');

    const grid = {
      x: product.x + product.w * (66 / crop.sw),
      y: product.y + product.h * (94 / crop.sh),
      w: product.w * (394 / crop.sw),
      h: product.h * (379 / crop.sh)
    };
    const cellW = grid.w / 3;
    const cellH = grid.h / 3;
    photos.forEach((photo, index) => {
      const x = grid.x + (index % 3) * cellW;
      const y = grid.y + Math.floor(index / 3) * cellH;
      if (photo) drawPhoto(ctx, photo, x, y, cellW, cellH);
      else drawPlaceholder(ctx, x, y, cellW, cellH, index + 1);
    });

    const discSize = product.w * (381 / crop.sw) * .70;
    const discX = grid.x + (grid.w - discSize) / 2;
    const discY = grid.y + (grid.h - discSize) / 2;
    const discCenterX = discX + discSize / 2;
    const discCenterY = discY + discSize / 2;
    if (state.productReady) {
      const discSource = { sx: 709, sy: 587, sw: 258, sh: 258 };
      ctx.save();
      ctx.beginPath();
      ctx.arc(discCenterX, discCenterY, discSize / 2 - 1, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(state.productImage, discSource.sx, discSource.sy, discSource.sw, discSource.sh, discX, discY, discSize, discSize);
      ctx.restore();
    } else {
      ctx.fillStyle = '#050505';
      ctx.beginPath();
      ctx.arc(discCenterX, discCenterY, discSize / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.save();
    ctx.strokeStyle = '#020202';
    ctx.lineCap = 'round';
    ctx.lineWidth = discSize * .16;
    ctx.beginPath();
    ctx.arc(discCenterX, discCenterY, discSize * .405, -1.9, 1.9);
    ctx.stroke();
    ctx.restore();
    drawArcText(ctx, 'PLEASE PUT IN THE MUSIC ALBUM YOU WANT TO PLAY', discCenterX, discCenterY, discSize * .45, discSize * .035);

    ctx.fillStyle = '#151311';
    ctx.font = '800 31px Arial, sans-serif';
    ctx.fillText('9 MINI VINYL COVERS', 885, 165);
    ctx.fillStyle = '#776d64';
    ctx.font = '500 18px Arial, sans-serif';
    ctx.fillText('Each photo is center-cropped onto its matching disc.', 887, 198);
    photos.forEach((photo, index) => {
      const cx = 932 + (index % 3) * 245;
      const cy = 315 + Math.floor(index / 3) * 245;
      drawVinyl(ctx, photo, cx, cy, 92, index + 1);
      ctx.fillStyle = '#7c7065';
      ctx.font = '800 15px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String(index + 1).padStart(2, '0'), cx, cy + 120);
    });
    ctx.textAlign = 'left';
    drawRoundedRect(ctx, 870, 925, 650, 48, 24, 'rgba(255,255,255,.5)', 'rgba(140,119,98,.2)');
    ctx.fillStyle = '#4f463f';
    ctx.font = '600 16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Photos 01–09 map to the grid and vinyls in the same order', 1195, 956);
    ctx.textAlign = 'left';
  }

  function renderSlots() {
    const slots = $('[data-mc-slots]');
    if (!slots) return;
    slots.replaceChildren();
    state.photos.forEach((photo, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mc-slot';
      button.setAttribute('aria-label', photo ? `Remove photo ${index + 1}` : `Photo slot ${index + 1}`);
      button.innerHTML = photo
        ? `<img src="${photo.url}" alt="Photo ${index + 1}"><span class="mc-slot__number">${String(index + 1).padStart(2, '0')}</span><span class="mc-slot__remove" aria-hidden="true">×</span>`
        : `<span class="mc-slot__empty">${String(index + 1).padStart(2, '0')}</span>`;
      if (photo) button.addEventListener('click', () => removePhoto(index));
      slots.appendChild(button);
    });
  }

  function renderAll() {
    renderSlots();
    if (!state.renderQueued) {
      state.renderQueued = true;
      requestAnimationFrame(renderCanvas);
    }
  }

  function downloadPreview() {
    const canvas = $('[data-mc-canvas]');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'Magneo-Custom-Music-Box-Preview.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  function bindEvents() {
    const picker = $('[data-mc-picker]');
    const dropzone = $('[data-mc-dropzone]');
    $('[data-mc-choose]')?.addEventListener('click', () => picker?.click());
    picker?.addEventListener('change', event => {
      acceptFiles(event.target.files);
      event.target.value = '';
    });
    $('[data-mc-download]')?.addEventListener('click', downloadPreview);
    ['dragenter', 'dragover'].forEach(type => dropzone?.addEventListener(type, event => {
      event.preventDefault();
      dropzone.classList.add('is-dragging');
    }));
    ['dragleave', 'drop'].forEach(type => dropzone?.addEventListener(type, event => {
      event.preventDefault();
      dropzone.classList.remove('is-dragging');
    }));
    dropzone?.addEventListener('drop', event => acceptFiles(event.dataTransfer?.files));
  }

  async function init() {
    bindEvents();
    renderAll();
    if (!PRODUCT_ASSET) return;
    try {
      state.productImage = await loadImage(PRODUCT_ASSET);
      state.productReady = true;
      renderAll();
    } catch (error) {
      setError('The product preview image could not be loaded. You can still preview your photos.');
    }
  }

  init();
})();
